import {
  Suggest,
  HotelSearchCriteria,
  Lang,
  IHotelSearchCriteria,
} from 'types';
import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { parse, format, isValid, differenceInDays } from 'date-fns';

import i18n from '../../plugins/i18n';
import { SelectedHotelPersistenceData } from 'api';

const DATE_OPTIONS = { month: 'short', day: '2-digit' };

export class State {
  criteria = new HotelSearchCriteria();
  // local = new HotelSearchCriteria();
  destination: string = '';
}

const getters = <GetterTree<State, any>>{
  adults(state: State): number {
    return state.criteria.adults;
  },

  checkin(state: State): Date {
    return parse(state.criteria.checkin);
  },

  checkout(state: State): Date {
    return parse(state.criteria.checkout);
  },

  id(state: State): string {
    return state.criteria.id;
  },

  nights(state: State): number {
    return state.criteria.getNights();
  },

  inAndOutDates(state: State, getters, rootState): string {
    const lang =
      rootState.support.lang && rootState.support.lang.locale
        ? rootState.support.lang.locale
        : Lang.DEFAULT_LANG().locale;
    return (
      parse(state.criteria.checkin).toLocaleString(lang, DATE_OPTIONS as any) +
      ' - ' +
      parse(state.criteria.checkout).toLocaleString(lang, DATE_OPTIONS as any)
    );
  },

  // IsSearchAndLocalSame(state: State): boolean {
  //   const childsSorted = state.local.children.slice().sort();
  //   const previousSearchChildsSorted = state.criteria.children.slice().sort();
  //   return (
  //     state.local.checkin === state.criteria.checkin &&
  //     state.local.checkout === state.criteria.checkout &&
  //     state.local.adults === state.criteria.adults &&
  //     state.local.id === state.criteria.id &&
  //     childsSorted.length === previousSearchChildsSorted.length &&
  //     childsSorted.every((value, index) => {
  //       return value === previousSearchChildsSorted[index];
  //     })
  //   );
  // },

  getMobileHeaderTitle(state: State, getters, rootState): string {
    const lang =
      rootState.support.lang && rootState.support.lang.locale
        ? rootState.support.lang.locale
        : Lang.DEFAULT_LANG().locale;
    const dates =
      parse(state.criteria.checkin).toLocaleString(lang, DATE_OPTIONS as any) +
      ' - ' +
      parse(state.criteria.checkout).toLocaleString(lang, DATE_OPTIONS as any);

    const travelers =
      state.criteria.adults +
      state.criteria.children.filter(it => typeof it === 'number').length;
    return `${dates}, ${i18n.global.t(
      'searchForm.travelersPopup.travelers',
      travelers,
    )}`;
  },
};

const mutations = <MutationTree<State>>{
  updateSearchCriteria(state: State, criteria: IHotelSearchCriteria) {
    state.criteria = state.criteria.copyWith(criteria);
  },

  setAdults(state: State, count: number) {
    state.criteria.adults =
      count && count > 0 && count < 5
        ? count
        : HotelSearchCriteria.defaultAdults;
  },

  setCheckin(state: State, checkin: Date) {
    const date = format(checkin, HotelSearchCriteria.datePattern);
    state.criteria.checkin = date;
  },

  setCheckout(state: State, checkout: Date) {
    const date = format(checkout, HotelSearchCriteria.datePattern);
    state.criteria.checkout = date;
  },

  setChildren(state: State, children: number[]) {
    state.criteria.children = children;
  },

  addChild(state: State) {
    if (!Array.isArray(state.criteria.children)) {
      state.criteria.children = [];
    }

    state.criteria.children.push(null);
  },

  removeChild(state: State) {
    const lastNullIndex = state.criteria.children.lastIndexOf(null);

    if (lastNullIndex !== -1) {
      state.criteria.children.splice(lastNullIndex, 1);
    } else {
      state.criteria.children.pop();
    }
  },

  setChildAge(state: State, payload: { index: number; age: number }) {
    const { index, age } = payload;

    if (
      Array.isArray(state.criteria.children) &&
      index >= 0 &&
      index < state.criteria.children.length &&
      typeof age === 'number' &&
      age >= 0
    ) {
      state.criteria.children[index] = age;
    }
  },

  setId(state: State, payload: { id: string; hotelId?: string }) {
    // state.local.id = payload.id;
    // state.local.hotelId = payload.hotelId;
    state.criteria.id = payload.id;
    state.criteria.hotelId = payload.hotelId;
  },

  setDestination(state: State, d: string) {
    state.destination = d;
  },

  setSearchCriteria(state: State, searchCriteria: HotelSearchCriteria) {
    state.criteria = searchCriteria;
  },

  setLocalSearchCriteria(state: State, searchCriteria: HotelSearchCriteria) {
    // state.local = searchCriteria;
  },
};

const actions = <ActionTree<State, any>>{
  initSearchCriteria(store: ActionContext<State, any>, route) {
    const query = route.query;

    const id = route.params.id ? route.params.id : '';
    const pageArgs = SelectedHotelPersistenceData.getPersistanceById(id);

    let coordinates = parseIdToCoordinates(id);

    if (
      !query.in ||
      query.in.length != 10 ||
      !query.out ||
      query.out.length != 10
    ) {
      store.dispatch('initDefaultSearchCriteria', id);
      return;
    }

    const checkin = parse(query.in);
    const checkout = parse(query.out);

    if (
      !isValid(checkin) ||
      !isValid(checkout) ||
      differenceInDays(checkin, new Date()) < 0
    ) {
      store.dispatch('initDefaultSearchCriteria', id);
      return;
    }

    let adults = query.guests
      ? +query.guests
      : HotelSearchCriteria.defaultAdults;
    adults =
      adults < 1 || adults > 5 ? HotelSearchCriteria.defaultAdults : adults;

    const children =
      query?.childs
        ?.toString()
        ?.split(',')
        ?.map(age => parseInt(age.trim()))
        ?.filter(age => !isNaN(age) && age >= 0) ?? [];

    const geo = query.geo ? query.geo : '';
    const hotelArgs = pageArgs?.hotel;

    const queryDestination =
      hotelArgs != null
        ? hotelArgs.name ?? ''
        : query.d
        ? decodeURIComponent(query.d)
        : '';

    const isQueryDestinationUndefined =
      queryDestination &&
      (queryDestination === 'undefined' || queryDestination === 'null');
    const destination =
      queryDestination && !isQueryDestinationUndefined ? queryDestination : '';

    const hotelId = query.hotelId;
    const hotelName = query.hotel;

    store.commit(
      'setSearchCriteria',
      new HotelSearchCriteria({
        checkin: format(checkin, HotelSearchCriteria.datePattern),
        checkout: format(checkout, HotelSearchCriteria.datePattern),
        adults,
        id,
        geo,
        hotelId,
        coordinates,
        children,
      }),
    );

    store.commit(
      'setLocalSearchCriteria',
      new HotelSearchCriteria({
        checkin: format(checkin, HotelSearchCriteria.datePattern),
        checkout: format(checkout, HotelSearchCriteria.datePattern),
        adults,
        id,
        geo,
        hotelId,
        coordinates,
        children,
      }),
    );

    store.commit('setDestination', destination);

    const suggestType = query.type ? query.type : '';

    let locationType = Suggest.LocationType.UNKONWN;

    switch (suggestType) {
      case 'city':
        locationType = Suggest.LocationType.CITY;
        break;
      case 'airport':
        locationType = Suggest.LocationType.AIRPORT;
        break;
      case 'hotel':
        locationType = Suggest.LocationType.HOTEL;
        break;
      case 'station':
        locationType = Suggest.LocationType.STATION;
        break;
      default:
        locationType = Suggest.LocationType.UNKONWN;
        break;
    }

    const title =
      locationType === Suggest.LocationType.HOTEL ? hotelName : destination;

    const source: Suggest.HotelSuggest = {
      title,
      id: hotelId ?? id,
      type: suggestType,
      sub_title: destination,
    };

    if (coordinates) {
      source.lat = coordinates.lat;
      source.lon = coordinates.lon;
    }

    const suggest: Suggest.Suggest<Suggest.HotelSuggest> = {
      locationType,
      source,
      title: destination,
    };

    if (hotelName) {
      suggest.subtitle = hotelName;
    }

    store.dispatch(
      'suggest/setSuggest',
      { id: Suggest.Ids.HOTEL, suggest: suggest },
      { root: true },
    );
  },

  initDefaultSearchCriteria(store: ActionContext<State, any>, id?: string) {
    store.commit(
      'setSearchCriteria',
      new HotelSearchCriteria({
        id: id ? id : '',
      }),
    );
    store.commit(
      'setLocalSearchCriteria',
      new HotelSearchCriteria({
        id: id ? id : '',
      }),
    );
  },
};

export const parseIdToCoordinates = (id: string) => {
  try {
    if (!id.includes('G')) {
      throw new Error('Not geo id');
    }

    const parts = id.split(',');
    const lat = parseFloat(parts[0].slice(1));
    const lon = parseFloat(parts[1]);
    return { lat, lon };
  } catch {
    return null;
  }
};

export const searchCriteria = {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions,
};

export default {};
